import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ActionContainer,
  EndShiftButton
} from './styles';

import ActionModal from '../../../../components/ActionModal';
import CancelShift from '../../../FacilityShift/List/components/ShiftActions/components/CancelShift';
import {
  cancelShiftRequest,
  selectCancelShiftRequestLoading,
  selectCancelShiftRequestSuccess,
  selectCancelShiftRequestError,
  selectCancelShiftRequestMessage,
} from '../../../../modules/shift';
import ConfirmationDialog from '../../../../components/common/ConfirmationDialog';
import AlertPopup from '../../../../components/common/AlertPopup';

interface ShiftRequestActionProps {
  status: string;
  shiftRequestId: number;
}

const ShiftRequestActions: React.FC<ShiftRequestActionProps> = ({
  status,
  shiftRequestId
}) => {
  const dispatch = useDispatch<any>();
  // Allow cancellation if status is 'requested'
  const canCancel = (status === 'requested');

  const loading = useSelector(selectCancelShiftRequestLoading);
  const success = useSelector(selectCancelShiftRequestSuccess);
  const error = useSelector(selectCancelShiftRequestError);
  const message = useSelector(selectCancelShiftRequestMessage);

  const [openActionModal, setOpenActionModal] = React.useState(false);
  const [openConfirmation, setOpenConfirmation] = React.useState(false);
  const [cancelData, setCancelData] = React.useState<{ reason: string; notes: string } | null>(null);
  const [openAlert, setOpenAlert] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState('');
  const [alertSeverity, setAlertSeverity] = React.useState<'success' | 'error'>('success');

  const onEndShift = () => {
    setOpenActionModal(true);
  }

  const onCancelShift = (data: { reason: string; notes: string }) => {
    setCancelData(data);
    setOpenConfirmation(true);
  };

  const handleConfirmCancel = () => {
    if (cancelData) {
      dispatch(cancelShiftRequest({
        shiftRequestId,
        data: { cancellation_reason: cancelData.reason }
      }));
    }
    setOpenConfirmation(false);
  };

  useEffect(() => {
    if (success) {
      setOpenActionModal(false);
      setAlertMessage(message || 'Shift request cancelled successfully');
      setAlertSeverity('success');
      setOpenAlert(true);
    }
  }, [success, message]);

  useEffect(() => {
    if (error) {
      setAlertMessage(error || 'Failed to cancel shift request');
      setAlertSeverity('error');
      setOpenAlert(true);
    }
  }, [error]);

  if (!canCancel) return null;

  return (
    <ActionContainer>
      <EndShiftButton
        onClick={onEndShift}
        disabled={loading}
      >
        {loading ? 'Cancelling Request...' : 'Cancel Request'}
      </EndShiftButton>

      <ActionModal
        open={openActionModal}
        onClose={() => setOpenActionModal(false)}
        height="70vh"
      >
        <CancelShift
          onCancelShift={onCancelShift}
          onKeepShift={() => setOpenActionModal(false)}
          isLateCancellation={false} // Assuming shift requests don't have late cancellation charges context yet or simpler flow
        />
      </ActionModal>

      <ConfirmationDialog
        open={openConfirmation}
        onClose={() => setOpenConfirmation(false)}
        onConfirm={handleConfirmCancel}
        title="Confirm Cancellation"
        message="Are you sure you want to cancel this shift request?"
      />

      <AlertPopup
        open={openAlert}
        message={alertMessage}
        severity={alertSeverity}
        onClose={() => setOpenAlert(false)}
      />
    </ActionContainer>
  );
};

export default ShiftRequestActions;
